import { Component, EventEmitter, inject, Input, Output, ViewChild } from '@angular/core';
import { CredentialType } from 'enums/CredentialType';
import { Inputs } from 'modules/inputs';
import { Layout } from 'modules/layout';
import { AccountingService } from 'services/accounting.service';
import { CredentialRequired } from 'services/credential.service';
import { CompanyDataProviderFactory } from 'services/company-data-provider';
import { ProductPlanModel } from 'models/generated/Entities.GormCrm.ProductPlan';
import { BrandService } from 'services/brand.service';
import { BrandModel } from 'models/generated/Entities.GormCrm.Brand';
import { AccountingPlanModel } from 'models/generated/Entities.Accounting.AccountingPlan';
import { ListView } from 'modules/list-view';
import { Pipes } from 'modules/pipes';
import { InvoicingService, LedgerEntryDisplayModel } from 'services/invoicing.service';
import { BcDocumentType } from 'enums/BcDocumentType';
import { LegalCaseService } from 'services/legal-case.service';
import { LegalCaseModel } from 'models/generated/Entities.GormCrm.LegalCase';
import { LegalCaseStatus } from 'enums/LegalCaseStatus';
import { ButtonComponent } from 'components/input/button/button.component';

@CredentialRequired(CredentialType.EditLegalCases)
@Component({
	selector: 'edit-legal-case',
	templateUrl: './edit-legal-case.component.html',
	imports: [Layout, Inputs, ListView, Pipes]
})
export class EditLegalCaseComponent {

	private readonly brandService = inject(BrandService);
	private readonly accountingService = inject(AccountingService);
	private readonly invoicingService = inject(InvoicingService);
	private readonly dataProvider = inject(CompanyDataProviderFactory).getProviderForCurrentScope();
	private readonly legalCaseService = inject(LegalCaseService);

	private _openInvoices?: LedgerEntryDisplayModel[];
	private _selectedBrand?: BrandModel;
	private _suspendAllBrands?: boolean;

	public set selectedBrand(brand: BrandModel | undefined) { this._selectedBrand = brand; this.refreshLists(); }
	public get selectedBrand() { return this._selectedBrand; }
	public set suspendAllBrands(value: boolean | undefined) { this._suspendAllBrands = value; this.refreshLists(); }
	public get suspendAllBrands() { return this._suspendAllBrands; }
	public usedBrands?: BrandModel[];
	public plansToSuspend?: ProductPlanModel[];
	public invoicesToHold?: LedgerEntryDisplayModel[];
	public activeAccountingPlans = new Map<ProductPlanModel, AccountingPlanModel>();
	public bcDocumentType = BcDocumentType;

	@ViewChild('closeCaseButton') private closeCaseButton?: ButtonComponent;
	private allPlans?: ProductPlanModel[];
	private set openInvoices(invoices: LedgerEntryDisplayModel[]) {
		const brands = this.brandService.brands;
		this._openInvoices = invoices;
		this.usedBrands = [];
		for (const invoice of invoices) {
			const brand = brands.find(b => b.id == invoice.brandId);
			if (brand && this.usedBrands.indexOf(brand) < 0) {
				this.usedBrands.push(brand);
			}
		}
		if (!this.selectedBrand && this.usedBrands.length == 1) this.selectedBrand = this.usedBrands[0];
	}

	private _legalCase?: LegalCaseModel;
	@Input()
	set legalCase(legalCase: LegalCaseModel | undefined) {
		this._legalCase = legalCase;
		this.selectedBrand = this.brandService.brands.find(b => b.id == legalCase?.brandId);
	}
	get legalCase() { return this._legalCase; }
	get companyId() { return this.dataProvider.companyId; }

	@Output() close = new EventEmitter<void>();

	private refreshLists() {
		this.invoicesToHold = this._openInvoices?.filter(invoice => !this.selectedBrand || invoice.brandId == this.selectedBrand.id);
		this.plansToSuspend = this.legalCase
			? this.allPlans?.filter(plan => this.legalCase?.legalCaseProductPlans.some(p => p.productPlanId == plan.id))
			: this.allPlans?.filter(plan => this.accountingService.isActive(this.activeAccountingPlans.get(plan)))
				.filter(plan => !this.selectedBrand || this.suspendAllBrands === true || plan.brandId == this.selectedBrand.id);
	}

	constructor() {
		this.loadInvoices();
		this.loadProductPlans();
	}

	private async loadProductPlans() {
		if (!this.dataProvider) return;
		const productPlans = await this.dataProvider.getAllProductPlansAndAccountingPlans();

		this.activeAccountingPlans.clear();
		for (const productPlan of productPlans) {
			const accountingPlan = this.accountingService.filterActiveAccountingPlan(productPlan.accountingPlans);
			this.activeAccountingPlans.set(productPlan, accountingPlan);
		}
		this.allPlans = productPlans;
		this.refreshLists();
	}

	private async loadInvoices() {
		if (!this.companyId) return;
		const ledgerEntries = await this.invoicingService.getLedgerEntriesForCompany(this.companyId);
		this.openInvoices = ledgerEntries.filter(le => le.documentType == BcDocumentType.Invoice && le.open === true);
		this.refreshLists();
	}

	public cancel() {
		this.close.emit();
	}

	async createDebtCollection() {
		if (!this.companyId || !this.invoicesToHold || this.invoicesToHold.length == 0 || !this.plansToSuspend || !this.selectedBrand) return;

		await this.legalCaseService.createLegalCase(this.companyId, this.selectedBrand.id!, this.plansToSuspend.map(p => p.id!), this.invoicesToHold.map(i => i.documentNo));
		this.close.emit();
	}

	public legalCaseIsOpen() { return this.legalCase?.status !== LegalCaseStatus.Closed; }
	public async closeLegalCase() {
		if (!this.legalCase || !this.invoicesToHold) return;
		await this.legalCaseService.closeLegalCase(this.legalCase.id!, this.invoicesToHold.map(i => i.documentNo));
		this.closeCaseButton?.reset();
		this.legalCase.status = LegalCaseStatus.Closed;
		this.close.emit();
	}
}
